import * as Atomic from '../atomic';
import {
  NameCheck,
  DependencyNameCheck,
  OwnedMethodEventOwningCheck,
  MPCPathsCheck,
  MPCConflictCheck,
  InheritedCreateMethodCheck,
  ObjectAttributeNameConflictCheck,
} from '../../../checks/specific';
import {
  ObjectDuplicateCheck,
  EventDuplicateCheck,
  MethodDuplicateCheck,
  DependencyDuplicateCheck,
  DataTypeDuplicateCheck,
  AttributeDuplicateCheck,
  AcquiredMethodDuplicateCheck,
  StateDuplicateCheck,
  FSMDuplicateCheck,
  MPCDuplicateCheck,
} from '../../../checks/duplicates';
import { DependencyLoopCheck, InheritanceLoopCheck } from '../../../checks/general';
import {
  DependencyConsistencyCheck,
  AcquiredMethodConsistencyCheck,
  MPCConsistencyCheck,
  TransitionConsistencyCheck,
  InheritedMethodConsistencyCheck,
  SpecialisedMethodConsistencyCheck,
  EventConsistencyCheck,
} from '../../../checks/consistency';
import { ACMC_DataType, ACMC_Attribute } from '../index';
import {
  ensureObject,
  ensureEvent,
  ensureDependency,
  ensureMethod,
  ensureFSM,
  ensureState,
  ensureInheritance,
  uniqueName,
  isObjectType,
  isInheritance,
} from '../util';
import { upperFirst, orderBy } from 'lodash';
import { CEMC_Object, CEMC_SpecialiseMethod, CEMC_Transition } from './edit';
import { calculate as calculateFSM } from './calculate_fsm';
import { Combined_SpecialiseEvent } from '../../combined';
import type { IModel } from '../../types';
import type {
  AnyCheck,
  AutoEventsAndMethods,
  DependencyType,
  FSMType,
  MethodProperties,
  MethodType,
  MPCType,
  ObjectProperties,
  OrId,
  Specialisations,
  StateType,
} from '../../types';
import type { IObjectType } from '../../../../stores/model/metamodel/object_type';
import type { IEventType } from '../../../../stores/model/metamodel/event_type';
import type { IMethod } from '../../../../stores/model/metamodel/method';
import type { IDependency } from '../../../../stores/model/metamodel/dependency';
import type { IInheritance } from '../../../../stores/model/metamodel/inheritance';
import type { IFSM } from '../../../../stores/model/metamodel/fsm';
import type { IState } from '../../../../stores/model/metamodel/state';
import type { ITransition } from '../../../../stores/model/metamodel/transition';
import type { IDataType } from '../../../../stores/model/metamodel/data_type';
import type { AttributeOwnerLike } from '../../types';

/**
 * Get either
 *  * the existing event with the given name
 *  * a new event with the given name + needed checks added to the checks.
 */
function getOrCreateEvent(
  model: IModel,
  checks: AnyCheck[],
  name: string
): { event: IEventType; created: boolean } {
  const metamodel = model.metamodel;
  if (metamodel.eventNames.includes(name)) {
    return {
      // Guarded by eventNames.includes(name), so the lookup always resolves.
      event: metamodel.getEventByName(name)!,
      created: false,
    };
  } else {
    const { event, checks: extraChecks } = CCMC_Event(model, name);
    checks.push(...extraChecks);
    return {
      event,
      created: true,
    };
  }
}

/**
 * Create a new object type and optionally also create and end events and
 * methods.
 *
 * @param {model} model The model to operate on
 * @param {Object} properties The properties for the object
 * - {String} name: The name for the new object type
 * - {String} description: The description for the new object type
 * - {Boolean} abstract: Whether the object type is abstract or not (currently
 *   always false since inheritence is not yet supported)
 * @param {Object} events_and_methods The information about the methods and
 * events to create.
 * - {Boolean} auto: should the methods and events be created automatically? If
 *   false, the arguments below are optional.
 * - {String} create_event_name: the name for the CREATE event
 * - {String} end_event_name: the name for the END event
 * - {String} create_method_name: the name for the CREATE method
 * - {String} end_method_name: the name for the END method
 */
export function CCMC_Object(
  model: IModel,
  { name, description, abstract }: ObjectProperties,
  { auto, create_event_name, end_event_name, create_method_name, end_method_name }: AutoEventsAndMethods
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];
  const events: IEventType[] = [];
  const methods: IMethod[] = [];

  // Execute the atomic commands and prepare the checks
  const object = Atomic.ACMC_Object(metamodel, name, description, abstract);
  checks.push(new NameCheck(metamodel, object), new ObjectDuplicateCheck(metamodel, object));
  if (auto) {
    // Automatically create events and methods too
    const { event: cr_event, created: cr_event_created } = getOrCreateEvent(
      model,
      checks,
      create_event_name
    );
    if (cr_event_created) {
      events.push(cr_event);
    }
    const { event: end_event, created: end_event_created } = getOrCreateEvent(
      model,
      checks,
      end_event_name
    );
    if (end_event_created) {
      events.push(end_event);
    }
    const cr_method = Atomic.ACMC_OwnedMethod(
      metamodel,
      create_method_name,
      object,
      cr_event,
      'CREATE'
    );
    const end_method = Atomic.ACMC_OwnedMethod(metamodel, end_method_name, object, end_event, 'END');
    methods.push(cr_method, end_method);
    checks.push(
      new NameCheck(metamodel, cr_method),
      new MethodDuplicateCheck(metamodel, cr_method),
      new NameCheck(metamodel, end_method),
      new MethodDuplicateCheck(metamodel, end_method)
    );
  }

  // Add a default FSM
  const { fsm, checks: fsmChecks } = CCMC_FSM(model, object, 'DEFAULT', 'DEFAULT', true);
  checks.push(...fsmChecks);

  return {
    object,
    checks,
    events,
    methods,
    fsm,
  };
}

/**
 * Create a dependency and optionally propagate methods along the newly created
 * dependency.
 *
 * @param {Model} model The model to operate on.
 * @param {String} masterId The id of the master.
 * @param {String} dependentId The id of the dependent.
 * @param {DEPENDNCY_TYPE} type The dependency type.
 * @param {Boolean} propagate Automatically propagate methods via the
 * dependency?
 */
export function CCMC_Dependency(
  model: IModel,
  masterId: string,
  dependentId: string,
  type: DependencyType,
  propagate: boolean
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];
  // Find the master and dependent
  // They should exist (otherwise our UI is broken), so resolve non-null.
  const master = metamodel.getObjectTypeById(masterId)!;
  const dependent = metamodel.getObjectTypeById(dependentId)!;

  // NOTE: do we need to check if master/dependent exist? They should exists
  // otherwise our UI is broken.

  // Construct the name
  const name = { master: master.name, dependent: dependent.name };
  // Check if it is already taken
  const peers = master.dependenciesAsMaster.filter((d) => d.dependent === dependent);
  if (peers.length > 0) {
    let suffix = 1;
    // suffix < 100 => better be safe than sorry
    while (
      peers.some((d) => d.name.master === name.master && d.name.dependent === name.dependent) &&
      suffix < 100
    ) {
      name.master = `${master.name}${suffix}`;
      name.dependent = `${dependent.name}${suffix}`;
      suffix++;
    }
  }

  // Execute the atomic command and prepare the checks
  const dependency = Atomic.ACMC_Dependency(metamodel, name, master, dependent, type);
  checks.push(
    new DependencyNameCheck(metamodel, dependency),
    new DependencyConsistencyCheck(metamodel, dependency),
    new DependencyDuplicateCheck(metamodel, dependency),
    new DependencyLoopCheck(metamodel)
  );
  for (const attribute of dependent.attributes.concat(
    dependent.indirectSubtypes.flatMap((st) => st.attributes.slice())
  )) {
    // We might introduce a conflict with an attribute name of the dependent or any of its subtypes
    checks.push(new ObjectAttributeNameConflictCheck(metamodel, attribute));
  }
  for (const attribute of master.attributes.concat(
    master.indirectSubtypes.flatMap((st) => st.attributes.slice())
  )) {
    // We might introduce a conflict with an attribute name of the master or any of its subtypes
    checks.push(new ObjectAttributeNameConflictCheck(metamodel, attribute));
  }

  const methods: IMethod[] = [];
  const transitions: ITransition[] = [];
  if (propagate && !master.isExistenceDependentOf(dependent)) {
    // Automatically propagate methods along the newly added dependency
    const {
      methods: extraMethods,
      checks: extraChecks,
      transitions: extraTransitions,
    } = CCMC_AutoPropagateDependency(model, dependency);
    methods.push(...extraMethods);
    checks.push(...extraChecks);
    transitions.push(...extraTransitions);
  }

  // Also check event owning rule when we add a dependency!
  for (const method of dependency.findDownstreamOwnedMethods()) {
    checks.push(new OwnedMethodEventOwningCheck(metamodel, method));
  }

  return {
    dependency,
    methods,
    transitions,
    checks,
  };
}

/**
 * Create an event type.
 *
 * @param {Model} model The model to operate on.
 * @param {String} name The name for the new event type.
 */
export function CCMC_Event(model: IModel, name: string) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const event = Atomic.ACMC_Event(metamodel, name);
  checks.push(
    new NameCheck(metamodel, event),
    new EventDuplicateCheck(metamodel, event),
    new EventConsistencyCheck(metamodel, event)
  );

  return {
    event,
    checks,
  };
}

/**
 * Create specialised event type
 *
 * @param {Model} model The model to operate on.
 * @param {String} name The name for the new specialised event type.
 * @param {EventType or String} ownerEventTypeOrId The owner event type or its id.
 * @param {Inheritance or String} inheritanceOrId The inheritance of its id.
 */
export function CCMC_SpecialisedEvent(
  model: IModel,
  name: string,
  ownerEventTypeOrId: OrId<IEventType>,
  inheritanceOrId: OrId<IInheritance>
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const ownerEventType = ensureEvent(metamodel, ownerEventTypeOrId)!;
  const inheritance = ensureInheritance(metamodel, inheritanceOrId)!;

  const event = Atomic.ACME_SpecialisedEvent(metamodel, name, ownerEventType, inheritance);
  checks.push(
    new NameCheck(metamodel, event),
    new EventDuplicateCheck(metamodel, event),
    new EventConsistencyCheck(metamodel, event)
  );

  return {
    event,
    checks,
  };
}

/**
 * Add a new method and optionally automatically propagate it.
 *
 * @param {Model} model The model to operate on.
 * @param {Object} properties The properties of the new method.
 *  - {String} name: the name for the method
 *  - {EventType or String} ownerEventType: the owner event type (or its id)
 *  - {ObjectType or String} ownerObjectType: the owner object type (or its id)
 *  - {Dependency or String} viaDependency: the via dependency (or its id) [only
 *    for acquired methods]
 *  - {Method or String} viaMethod: the via method (or its id) [only for
 *    acquired, inherited or specialised methods]
 *  - {Inheritance or String} viaInheritance: the via inheritance (or its id) [only for
 *    inherited methods]
 *  - {METHOD_TYPE} type: the type of the method
 *  - {PROVENANCE} provenance: the provenance of the method
 * @param {Boolean} propagate Automatically propagate the method?
 */
export function CCMC_Method(model: IModel, properties: MethodProperties, propagate: boolean) {
  const {
    name,
    ownerEventType,
    ownerObjectType,
    viaDependency,
    viaMethod,
    viaInheritance,
    type,
    provenance,
  } = properties;
  if (provenance === 'ACQUIRED') {
    // For ACQUIRED methods the via* properties are required (see JSDoc).
    return CCMC_AcquiredMethod(
      model,
      name,
      ownerObjectType,
      viaDependency!,
      type,
      viaMethod!,
      ownerEventType,
      propagate
    );
  } else if (provenance === 'OWNED') {
    return CCMC_OwnedMethod(model, name, ownerObjectType, ownerEventType, type, propagate);
  } else if (provenance === 'INHERITED') {
    return CCMC_InheritedMethod(
      model,
      name,
      ownerObjectType,
      ownerEventType,
      type,
      viaMethod!,
      viaInheritance!,
      propagate
    );
  } else if (provenance === 'SPECIALISED') {
    return CCMC_SpecialisedMethod(
      model,
      name,
      ownerObjectType,
      ownerEventType,
      type,
      viaMethod!,
      propagate
    );
  } else {
    // We should never get here
    throw new Error('Incorrect method provenance');
  }
}

/**
 * Create an owned method and optionally propagate it automatically.
 *
 * @param {Model} model The model to operate on.
 * @param {String} name The name for the new method.
 * @param {ObjectType or String} ownerObjectTypeOrId The owner object type or
 * its id.
 * @param {EvenType or String} ownerEventTypeOrId The owner event type or its
 * id.
 * @param {METHOD_TYPE} type The type of the new method.
 * @param {Boolean} propagate Should the method be propagated automatically?
 */
export function CCMC_OwnedMethod(
  model: IModel,
  name: string,
  ownerObjectTypeOrId: OrId<IObjectType>,
  ownerEventTypeOrId: OrId<IEventType>,
  type: MethodType,
  propagate: boolean
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];
  const methods: IMethod[] = [];
  const transitions: ITransition[] = [];

  const ownerObjectType = ensureObject(metamodel, ownerObjectTypeOrId)!;
  const ownerEventType = ensureEvent(metamodel, ownerEventTypeOrId)!;

  // Create the method
  const method = Atomic.ACMC_OwnedMethod(metamodel, name, ownerObjectType, ownerEventType, type);
  methods.push(method);
  checks.push(
    new NameCheck(metamodel, method),
    new MethodDuplicateCheck(metamodel, method),
    new OwnedMethodEventOwningCheck(metamodel, method)
  );

  if (propagate) {
    // Automatically propagate the method
    const {
      methods: extraMethods,
      checks: extraChecks,
      transitions: extraTransitions,
    } = CCMC_AutoPropagateMethod(model, method);
    checks.push(...extraChecks);
    methods.push(...extraMethods);
    transitions.push(...extraTransitions);
  } else {
    // Propagation off: still add the method to its OWNER's default FSM so it does
    // not end up missing from the FSM (and trip FSMUsedEventsCheck) — issue #51.
    const { checks: fsmChecks, transitions: fsmTransitions } = CCMC_AddMethodToDefaultFSM(
      model,
      method
    );
    checks.push(...fsmChecks);
    transitions.push(...fsmTransitions);
  }

  return {
    checks,
    methods,
    transitions,
  };
}

/**
 * Create a new acquired method and optionally propagate it automatically.
 *
 * @param {Model} model The model to operate on.
 * @param {String} name The name for the method.
 * @param {ObjectType or String} ownerObjectTypeOrId The owner object type.
 * @param {Dependency or String} viaDependencyOrId The via dependency.
 * @param {METHOD_TYPE} type The type of method.
 * @param {Method or String} viaMethodOrId The via method.
 * @param {EventType or String} ownerEventTypeOrId The owner event type.
 * @param {Boolean} propagate Automatically propagate the new method?
 */
export function CCMC_AcquiredMethod(
  model: IModel,
  name: string,
  ownerObjectTypeOrId: OrId<IObjectType>,
  viaDependencyOrId: OrId<IDependency>,
  type: MethodType,
  viaMethodOrId: OrId<IMethod>,
  ownerEventTypeOrId: OrId<IEventType>,
  propagate: boolean
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];
  const methods: IMethod[] = [];
  const transitions: ITransition[] = [];

  const ownerObjectType = ensureObject(metamodel, ownerObjectTypeOrId)!;
  const ownerEventType = ensureEvent(metamodel, ownerEventTypeOrId)!;
  const viaDependency = ensureDependency(metamodel, viaDependencyOrId)!;
  const viaMethod = ensureMethod(metamodel, viaMethodOrId)!;

  // Create the method
  const method = Atomic.ACMC_AcquiredMethod(
    metamodel,
    name,
    ownerObjectType,
    ownerEventType,
    type,
    viaDependency,
    viaMethod
  );
  methods.push(method);
  checks.push(
    new NameCheck(metamodel, method),
    new MethodDuplicateCheck(metamodel, method),
    new AcquiredMethodDuplicateCheck(metamodel, method),
    new AcquiredMethodConsistencyCheck(metamodel, method)
  );

  if (propagate) {
    // Automatically propagate the new method.
    const {
      methods: extraMethods,
      checks: extraChecks,
      transitions: extraTransitions,
    } = CCMC_AutoPropagateMethod(model, method);
    checks.push(...extraChecks);
    methods.push(...extraMethods);
    transitions.push(...extraTransitions);
  } else {
    // Propagation off: still add the method to its OWNER's default FSM so it does
    // not end up missing from the FSM (and trip FSMUsedEventsCheck) — issue #51.
    const { checks: fsmChecks, transitions: fsmTransitions } = CCMC_AddMethodToDefaultFSM(
      model,
      method
    );
    checks.push(...fsmChecks);
    transitions.push(...fsmTransitions);
  }

  return {
    checks,
    methods,
    transitions,
  };
}

/**
 * Create a new inherited method and optionally propagate it automatically.
 *
 * @param {Model} model The model to operate on.
 * @param {String} name The name for the method.
 * @param {ObjectType or String} ownerObjectTypeOrId The owner object type.
 * @param {EventType or String} ownerEventTypeOrId The owner event type.
 * @param {METHOD_TYPE} type The type of method.
 * @param {Method or String} viaMethodOrId The via method.
 * @param {Inheritance or String} viaInheritanceOrId The via inheritance.
 * @param {Boolean} propagate Automatically propagate the new method?
 */
export function CCMC_InheritedMethod(
  model: IModel,
  name: string,
  ownerObjectTypeOrId: OrId<IObjectType>,
  ownerEventTypeOrId: OrId<IEventType>,
  type: MethodType,
  viaMethodOrId: OrId<IMethod>,
  viaInheritanceOrId: OrId<IInheritance>,
  propagate: boolean
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];
  const methods: IMethod[] = [];
  const transitions: ITransition[] = [];

  const ownerObjectType = ensureObject(metamodel, ownerObjectTypeOrId)!;
  const ownerEventType = ensureEvent(metamodel, ownerEventTypeOrId)!;
  const viaMethod = ensureMethod(metamodel, viaMethodOrId)!;
  const viaInheritance = ensureInheritance(metamodel, viaInheritanceOrId)!;

  // Create the method
  const method = Atomic.ACMC_InheritedMethod(
    metamodel,
    name,
    ownerObjectType,
    ownerEventType,
    type,
    viaMethod,
    viaInheritance
  );
  methods.push(method);
  checks.push(
    new NameCheck(metamodel, method),
    new MethodDuplicateCheck(metamodel, method),
    new InheritedMethodConsistencyCheck(metamodel, method),
    new InheritedCreateMethodCheck(metamodel, method)
  );

  if (propagate) {
    // Automatically propagate the new method.
    const {
      methods: extraMethods,
      checks: extraChecks,
      transitions: extraTransitions,
    } = CCMC_AutoPropagateMethod(model, method);
    checks.push(...extraChecks);
    methods.push(...extraMethods);
    transitions.push(...extraTransitions);
  }

  return {
    checks,
    method,
    methods,
    transitions,
  };
}

/**
 * Create a new specialised method and optionally propagate it automatically.
 *
 * @param {Model} model The model to operate on.
 * @param {String} name The name for the method.
 * @param {ObjectType or String} ownerObjectTypeOrId The owner object type.
 * @param {EventType or String} ownerEventTypeOrId The (specialised) owner event type.
 * @param {METHOD_TYPE} type The type of method.
 * @param {Method or String} viaMethodOrId The via method.
 * @param {Boolean} propagate Automatically propagate the new method?
 */
export function CCMC_SpecialisedMethod(
  model: IModel,
  name: string,
  ownerObjectTypeOrId: OrId<IObjectType>,
  ownerEventTypeOrId: OrId<IEventType>,
  type: MethodType,
  viaMethodOrId: OrId<IMethod>,
  propagate: boolean
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];
  const methods: IMethod[] = [];
  const transitions: ITransition[] = [];

  const ownerObjectType = ensureObject(metamodel, ownerObjectTypeOrId)!;
  const ownerEventType = ensureEvent(metamodel, ownerEventTypeOrId)!;
  const viaMethod = ensureMethod(metamodel, viaMethodOrId)!;

  // Create the method
  const method = Atomic.ACMC_SpecialisedMethod(
    metamodel,
    name,
    ownerObjectType,
    ownerEventType,
    type,
    viaMethod
  );
  methods.push(method);
  checks.push(
    new NameCheck(metamodel, method),
    new MethodDuplicateCheck(metamodel, method),
    new SpecialisedMethodConsistencyCheck(metamodel, method)
  );

  // Replace the viaMethod by the new specialised method in the applicable
  // FSM transitions
  const transitionsToCheck = viaMethod.ownerObjectType.fsms.flatMap((fsm) =>
    fsm.transitions.slice()
  );
  for (const transition of transitionsToCheck) {
    if (transition.hasMethod(viaMethod)) {
      // Replace the viaMethod by the new specialisedMethod
      const newMethods = transition.metaMethods.filter((m) => m !== viaMethod);
      newMethods.push(method);
      Atomic.AEMC_Transition(transition, newMethods);
      checks.push(new TransitionConsistencyCheck(model.metamodel, transition));
      // transition was not created,so no need to add it to 'transitions'
    }
  }

  if (propagate) {
    // Automatically propagate the new method.
    const {
      methods: extraMethods,
      checks: extraChecks,
      transitions: extraTransitions,
    } = CCMC_AutoPropagateMethod(model, method);
    checks.push(...extraChecks);
    methods.push(...extraMethods);
    transitions.push(...extraTransitions);
  }

  return {
    checks,
    methods,
    transitions,
  };
}

/**
 * Automatically propagate a method.
 *
 * The general rules are
 * 1. Inheritance is always done
 * 2. O/*, S/* and A/* methods are always propagated
 * 3. an I/* should not be propagated to an object type O if this object type O already contains an
 *    acquired method that appears in the propagation path.
 *
 * @param {Model} model The model to operate on.
 * @param {Method} method The method to propagate.
 */
export function CCMC_AutoPropagateMethod(model: IModel, method: IMethod) {
  const checks: AnyCheck[] = [];
  const methods: IMethod[] = [];
  const transitions: ITransition[] = [];

  // Find the dependencies to follow
  const owner = method.ownerObjectType;
  let dependenciesToFollow: IDependency[] = [];
  // Propagate method over own dependencies unless it is specialised
  if (!method.specialised) {
    dependenciesToFollow = dependenciesToFollow.concat(owner.dependenciesAsDependent.slice());
  }
  // Propagate method over dependencies of supertypes unless it is inherited
  if (!method.inherited) {
    dependenciesToFollow = dependenciesToFollow.concat(
      owner.indirectSupertypes.flatMap((st) => st.dependenciesAsDependent.slice())
    );
  }
  // Loop over the dependencies to follow
  for (const dependency of dependenciesToFollow) {
    // Rule 3: don't propagate I/* if there is already a propagation along the dependency
    if (!method.inherited || !method.propagatedViaDependency(dependency)) {
      // Find the acquired method if already exists
      const acquiredMethod = method.findAcquiredMethod(dependency);
      if (acquiredMethod === null) {
        // No acquired method yet, add one (and auto propagate it)
        const {
          methods: extraMethods,
          checks: extraChecks,
          transitions: extraTransitions,
        } = CCMC_AcquiredMethod(
          model,
          method.name,
          dependency.master,
          dependency,
          'MODIFY',
          method,
          method.ownerEventType,
          true
        );
        checks.push(...extraChecks);
        methods.push(...extraMethods);
        transitions.push(...extraTransitions);
      } else {
        // There is an acquired method already, recursively call ourselves for
        // that acquired method
        const {
          methods: extraMethods,
          checks: extraChecks,
          transitions: extraTransitions,
        } = CCMC_AutoPropagateMethod(model, acquiredMethod);
        checks.push(...extraChecks);
        methods.push(...extraMethods);
        transitions.push(...extraTransitions);
      }
    }
  }

  // Inherit method over own inheritances unless it is specialised
  if (!method.specialised) {
    for (const inheritance of owner.inheritancesAsSupertype) {
      // Check if the method is already propagated via the inheritance
      // Find the inherited method if it already exists
      const inheritedMethod = method.findInheritedMethod(inheritance);
      if (inheritedMethod === null) {
        // No inherited method yet, add one (and auto propagate it)
        const {
          methods: extraMethods,
          checks: extraChecks,
          transitions: extraTransitions,
        } = CCMC_InheritedMethod(
          model,
          method.name,
          inheritance.subtype,
          method.ownerEventType,
          method.type,
          method,
          inheritance,
          true
        );
        checks.push(...extraChecks);
        methods.push(...extraMethods);
        transitions.push(...extraTransitions);
      } else {
        // There is an inherited method already, recursively call ourselves for
        // that inherited method
        const {
          methods: extraMethods,
          checks: extraChecks,
          transitions: extraTransitions,
        } = CCMC_AutoPropagateMethod(model, inheritedMethod);
        checks.push(...extraChecks);
        methods.push(...extraMethods);
        transitions.push(...extraTransitions);
      }
    }
  }

  // Add the method to its owner's default FSM. This is decoupled from
  // propagation proper (cascading to OTHER object types along dependencies /
  // inheritances): a method must live in its own default FSM regardless.
  const { checks: fsmChecks, transitions: fsmTransitions } = CCMC_AddMethodToDefaultFSM(
    model,
    method
  );
  checks.push(...fsmChecks);
  transitions.push(...fsmTransitions);

  return {
    checks,
    methods,
    transitions,
  };
}

/**
 * Add a method to its owner's default FSM (every object type has one).
 *
 * Adding a method to its OWNER's default FSM is a local consistency concern that
 * is independent of method *propagation* (which cascades a method onto OTHER
 * object types along dependencies/inheritances). Keeping it here — separate from
 * `CCMC_AutoPropagateMethod` — lets the method-creation commands call it even
 * when the user turns propagation off, so a freshly created method still lands in
 * its own default FSM (issue #51). `CCMC_AutoPropagateMethod` still calls it too,
 * so the propagate=true path is unchanged.
 *
 * Idempotent: the `!transition.hasMethod(method)` guard makes a repeated call for
 * the same method a no-op, so it is safe even if both this and the auto-propagate
 * path run for the same creation.
 *
 * Specialised (inherited + specialised) methods are intentionally skipped — they
 * are represented in the FSM by their specialisation, not directly.
 *
 * @param {Model} model The model to operate on.
 * @param {Method} method The method to add to its owner's default FSM.
 */
export function CCMC_AddMethodToDefaultFSM(model: IModel, method: IMethod) {
  const checks: AnyCheck[] = [];
  const transitions: ITransition[] = [];

  if (!method.specialised) {
    const owner = method.ownerObjectType;
    // Add the method to the default FSM (every object type has one).
    const fsm = owner.defaultFSM!;
    let source = fsm.initialState;
    let target = fsm.finalStates[0]; // default FSM only has 1 final state
    if (method.type !== 'CREATE') {
      source = fsm.intermediaryStates[0]; // default FSM only has 1 intermediary state
    }
    if (method.type !== 'END') {
      target = fsm.intermediaryStates[0]; // default FSM only has 1 intermediary state
    }
    const transition = fsm.findTransition(source!, target!);
    const methodsForTransition = [method];
    if (transition == null) {
      const { transition: newTransition, checks: extraChecks } = CCMC_Transition(
        model,
        owner,
        fsm,
        source!,
        target!,
        methodsForTransition
      );
      checks.push(...extraChecks);
      transitions.push(newTransition);
    } else if (
      // avoid adding twice: owned method that is converted to specialised method is passing twice
      // through this method, but may not be added to the FSM twice!
      !transition.hasMethod(method)
    ) {
      methodsForTransition.push(...transition.metaMethods);
      const sortedMethodsForTransition = orderBy(
        methodsForTransition,
        ['provenance', 'name'],
        ['desc', 'asc']
      );
      const { checks: extraChecks } = CEMC_Transition(
        model,
        owner,
        fsm,
        transition,
        sortedMethodsForTransition
      );
      checks.push(...extraChecks);
    }
  }

  return {
    checks,
    transitions,
  };
}

/**
 * Automatically propagate methods along a given dependency.
 *
 * @param {Model} model The model to operate on.
 * @param {Dependency} dependency The dependency to propagate along.
 */
export function CCMC_AutoPropagateDependency(model: IModel, dependency: IDependency) {
  const checks: AnyCheck[] = [];
  const methods: IMethod[] = [];
  const transitions: ITransition[] = [];

  // Propagate all methods of the dependent
  //   * filter out the inherited methods which are specialized
  let methodsToPropagate = dependency.dependent.methods.filter((m) => !m.specialised);
  //   * add the methods of the subtypes except inherited methods
  methodsToPropagate = methodsToPropagate.concat(
    dependency.dependent.indirectSubtypes
      .flatMap((st) => st.methods.slice())
      .filter((m) => !m.inherited)
  );
  for (const method of methodsToPropagate) {
    // Check if the method is inherited and already propagate along the dependency.
    if (!method.inherited || !method.propagatedViaDependency(dependency)) {
      // Find the acquired method
      const acquiredMethod = method.findAcquiredMethod(dependency);
      if (acquiredMethod === null) {
        // There is no acquired method yet: add one (and automatically propagate
        // it)
        const {
          methods: extraMethods,
          checks: extraChecks,
          transitions: extraTransitions,
        } = CCMC_AcquiredMethod(
          model,
          method.name,
          dependency.master,
          dependency,
          'MODIFY',
          method,
          method.ownerEventType,
          true
        );
        checks.push(...extraChecks);
        methods.push(...extraMethods);
        transitions.push(...extraTransitions);
      } else {
        // There is already an acquired method. Automatically propagate it.
        const {
          methods: extraMethods,
          checks: extraChecks,
          transitions: extraTransitions,
        } = CCMC_AutoPropagateMethod(model, acquiredMethod);
        checks.push(...extraChecks);
        methods.push(...extraMethods);
        transitions.push(...extraTransitions);
      }
    }
  }

  return {
    checks,
    methods,
    transitions,
  };
}

/**
 * Add a new data type.
 *
 * @param {Model} model The model to operate on.
 * @param {String} name The name for the new data type.
 */
export function CCMC_DataType(model: IModel, name: string) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const dataType = ACMC_DataType(metamodel, name);
  checks.push(new NameCheck(metamodel, dataType), new DataTypeDuplicateCheck(metamodel, dataType));

  return {
    checks,
    dataType,
  };
}

/**
 * Add a new attribute to the given attribute owner (i.e. object type, event
 * type or method).
 *
 * @param {Model} model The model to operate on.
 * @param {AttributeOwner} owner The owner.
 * @param {String} name The name of the new attribute.
 * @param {String} typeName The name of the attribute type.
 * @param {String} description The description (optional, default: '').
 * @param {String} implementation The implementation (optional, default: '').
 * @param {String} validation The validation (optional, default: '').
 */
export function CCMC_Attribute(
  model: IModel,
  owner: AttributeOwnerLike,
  name: string,
  typeName: string,
  description?: string,
  implementation?: string,
  validation?: string
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  // First determine the data type
  let dataType: IDataType | undefined;
  let typeArg: string;
  if (metamodel.isBuiltinDataType(typeName)) {
    // It's a builtin, ACMC_Attribute expects the name.
    typeArg = typeName;
  } else {
    // It's not a builtin, see if it is an existing custom data type.
    dataType = metamodel.getDataTypeByName(typeName);
    if (!dataType) {
      // No existing data type found, create one
      const { checks: extraChecks, dataType: newDataType } = CCMC_DataType(model, typeName);
      dataType = newDataType;
      checks.push(...extraChecks);
    }
    // For a custom data type, ACMC_Attribute expects the id.
    typeArg = dataType.id;
  }

  // Now create the attribute
  const attribute = ACMC_Attribute(
    owner,
    name,
    typeArg,
    description || '',
    implementation || '',
    validation || ''
  );
  checks.push(
    new NameCheck(metamodel, attribute),
    new AttributeDuplicateCheck(metamodel, attribute)
  );
  if (isObjectType(metamodel, owner)) {
    checks.push(new ObjectAttributeNameConflictCheck(metamodel, attribute));
  }

  return {
    checks,
    attribute,
    dataType,
  };
}

/**
 * Add a new multiple propagation constraint to the given object type.
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object type. The constraint may
 * be added to another object if the paths have overlapping tails.
 * @param {MPC_TYPE} type The type for the multiple propagation constraint.
 * @param {String[]} primaryPath The ids of the elements along the primary path
 * of the multiple propagation constraint.
 * @param {String[]} secondaryPath The ids of the elements along the secondary
 * path of the multiple propagation constraint.
 */
export function CCMC_MultiplePropagationConstraint(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  type: MPCType,
  primaryPath: string[],
  secondaryPath: string[]
) {
  const checks: AnyCheck[] = [];

  // Trim common tails from the path
  const trimmedPrimaryPath = primaryPath.slice();
  const trimmedSecondaryPath = secondaryPath.slice();

  while (
    trimmedPrimaryPath.length > 0 &&
    trimmedSecondaryPath.length > 0 &&
    trimmedPrimaryPath[trimmedPrimaryPath.length - 1] ===
      trimmedSecondaryPath[trimmedSecondaryPath.length - 1] &&
    !isInheritance(model.metamodel, trimmedPrimaryPath[trimmedPrimaryPath.length - 1])
  ) {
    trimmedPrimaryPath.pop();
    trimmedSecondaryPath.pop();
  }

  const lastPrimaryPathElementId = trimmedPrimaryPath[trimmedPrimaryPath.length - 1];
  const lastPrimaryPathElement =
    ensureDependency(model.metamodel, lastPrimaryPathElementId) ||
    ensureInheritance(model.metamodel, lastPrimaryPathElementId);
  // The last path element is always a resolvable dependency or inheritance.
  const object = lastPrimaryPathElement!.nextObject;

  const mpc = Atomic.ACMC_MultiplePropagationConstraint(
    object,
    type,
    trimmedPrimaryPath,
    trimmedSecondaryPath
  );

  checks.push(
    new MPCConsistencyCheck(model.metamodel, mpc),
    new MPCPathsCheck(model.metamodel, mpc),
    new MPCDuplicateCheck(model.metamodel, mpc),
    // No need to add MPCDisabledPathUsageCheck as it only generates warnings
    // and we don't abort because of warnings.
    new MPCConflictCheck(model.metamodel, mpc)
  );

  return {
    checks,
    multiplePropagationConstraint: mpc,
  };
}

/**
 * Add a new FSM to the given object type.
 *
 * If the new FSM is a default FSM, it also adds the default states and
 * transitions.
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object type.
 * @param {String} name The name for the new FSM.
 * @param {FSM_TYPE} type The type for the new FSM.
 * @param {Boolean} codegeneration Is the new FSM selected for codegeneration.
 */
export function CCMC_FSM(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  name: string,
  type: FSMType,
  codegeneration: boolean
) {
  const checks: AnyCheck[] = [];

  const object = ensureObject(model.metamodel, objectOrId)!;
  const fsm = Atomic.ACMC_FSM(object, name, type, codegeneration);
  if (fsm.type !== 'DEFAULT') {
    checks.push(new NameCheck(model.metamodel, fsm));
  }
  checks.push(new FSMDuplicateCheck(model.metamodel, fsm));

  if (type === 'DEFAULT') {
    // Add initial state
    const { state: initialState, checks: initStateChecks } = CCMC_State(
      model,
      object,
      fsm,
      'initial',
      'INITIAL_STATE'
    );
    checks.push(...initStateChecks);
    // Add exists state
    const { state: existsState, checks: existsStateChecks } = CCMC_State(
      model,
      object,
      fsm,
      'exists',
      'STATE'
    );
    checks.push(...existsStateChecks);
    // Add ended state
    const { state: endedState, checks: endedStateChecks } = CCMC_State(
      model,
      object,
      fsm,
      'ended',
      'FINAL_STATE'
    );
    checks.push(...endedStateChecks);

    // Add transitions
    const createMethods = object.methods.filter((m) => m.type === 'CREATE');
    const endMethods = object.methods.filter((m) => m.type === 'END');
    const modifyMethods = object.methods.filter((m) => m.type === 'MODIFY');
    if (createMethods.length > 0) {
      const { checks: createTransitionChecks } = CCMC_Transition(
        model,
        object,
        fsm,
        initialState,
        existsState,
        createMethods
      );
      checks.push(...createTransitionChecks);
    }
    if (endMethods.length > 0) {
      const { checks: endTransitionChecks } = CCMC_Transition(
        model,
        object,
        fsm,
        existsState,
        endedState,
        endMethods
      );
      checks.push(...endTransitionChecks);
    }
    if (modifyMethods.length > 0) {
      const { checks: modifyTransitionChecks } = CCMC_Transition(
        model,
        object,
        fsm,
        existsState,
        existsState,
        modifyMethods
      );
      checks.push(...modifyTransitionChecks);
    }
  }

  return {
    checks,
    fsm,
  };
}

/**
 * Copy an FSM
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object type.
 * @param {String} name The name for the new FSM.
 * @param {FSM_TYPE} type The type for the new FSM.
 * @param {Boolean} codegeneration Is the new FSM selected for codegeneration.
 * @param {String or FSM} copyFromOrId The FSM to copy from (or its id).
 */
export function CCMC_FSMCopy(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  name: string,
  type: FSMType,
  codegeneration: boolean,
  copyFromOrId: OrId<IFSM>
) {
  const checks: AnyCheck[] = [];

  const object = ensureObject(model.metamodel, objectOrId)!;
  const fsm = Atomic.ACMC_FSM(object, name, type, codegeneration);

  checks.push(new NameCheck(model.metamodel, fsm), new FSMDuplicateCheck(model.metamodel, fsm));

  const copyFrom = ensureFSM(object, copyFromOrId)!;
  const idMapping: Record<string, IState | ITransition> = {};

  // Copy the states
  for (const state of copyFrom.states) {
    const { state: newState, checks: newStateChecks } = CCMC_State(
      model,
      object,
      fsm,
      state.name,
      state.type
    );
    checks.push(...newStateChecks);
    idMapping[state.id] = newState;
  }
  // Copy the transitions
  for (const transition of copyFrom.transitions) {
    const methods = transition.metaMethods;
    // The source/target ids always map to copied states (states are inserted
    // before transitions in the loops above).
    const { checks: newTransitionChecks, transition: newTransition } = CCMC_Transition(
      model,
      object,
      fsm,
      idMapping[transition.sourceState.id] as IState,
      idMapping[transition.targetState.id] as IState,
      methods
    );
    checks.push(...newTransitionChecks);
    idMapping[transition.id] = newTransition;
  }

  return {
    checks,
    fsm,
    idMapping,
  };
}

/**
 * Calculate an FSM based on the two given FSMs
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object type.
 * @param {String or FSM} fsm1OrId The first FSM (or its id).
 * @param {String or FSM} fsm2OrId The second FSM (or its id).
 * @param {String} name The name for the new FSM.
 */
export function CCMC_CalculateFSM(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsm1OrId: OrId<IFSM>,
  fsm2OrId: OrId<IFSM>,
  name: string
) {
  const checks: AnyCheck[] = [];

  const object = ensureObject(model.metamodel, objectOrId)!;
  const fsm1 = ensureFSM(object, fsm1OrId)!;
  const fsm2 = ensureFSM(object, fsm2OrId)!;
  const fsm = Atomic.ACMC_FSM(object, name, 'CALCULATED', false);

  checks.push(new NameCheck(model.metamodel, fsm), new FSMDuplicateCheck(model.metamodel, fsm));

  const calculated = calculateFSM(fsm1, fsm2);

  // Add the calculated states
  const stateMap: Record<string, IState> = {};
  let counter = 1;
  for (const calculatedState of calculated.states) {
    let name = '';
    switch (calculatedState.type) {
      case 'INITIAL_STATE':
        name = 'initial';
        break;
      case 'FINAL_STATE':
        name = 'ended';
        break;
      case 'STATE':
      default:
        name = `state${counter++}`;
        break;
    }
    const { checks: stateChecks, state } = CCMC_State(
      model,
      object,
      fsm,
      name,
      calculatedState.type
    );
    checks.push(...stateChecks);
    stateMap[calculatedState.fingerprint] = state;
  }

  // Add the calculated transitions
  for (const calculatedTransition of calculated.transitions) {
    const source = stateMap[calculatedTransition.source.fingerprint];
    const target = stateMap[calculatedTransition.target.fingerprint];
    const { checks: transitionChecks } = CCMC_Transition(
      model,
      object,
      fsm,
      source,
      target,
      calculatedTransition.methods.map((m) => m.id)
    );
    checks.push(...transitionChecks);
  }

  return {
    checks,
    fsm,
  };
}

/**
 * Add a state to the given FSM
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object type.
 * @param {String or FSM} fsmOrId The FSM (or its id).
 * @param {String} name The name for the new state.
 * @param {STATE_TYPE} type The type for the new state.
 */
export function CCMC_State(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsmOrId: OrId<IFSM>,
  name: string,
  type: StateType
) {
  const checks: AnyCheck[] = [];

  const object = ensureObject(model.metamodel, objectOrId)!;
  const fsm = ensureFSM(object, fsmOrId)!;

  const state = Atomic.ACMC_State(fsm, name, type);

  checks.push(
    new NameCheck(model.metamodel, state),
    new StateDuplicateCheck(model.metamodel, state)
  );

  return {
    state,
    checks,
  };
}

/**
 * Add a transition to the given FSM
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object type.
 * @param {String or FSM} fsmOrId The FSM (or its id).
 * @param {String or State} sourceStateOrId The source state (or its id).
 * @param {String or State} targetStateOrId The target state (or its id).
 * @param {String[] or Method[]} methodsOrIds The methods (or their ids).
 */
export function CCMC_Transition(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsmOrId: OrId<IFSM>,
  sourceStateOrId: OrId<IState>,
  targetStateOrId: OrId<IState>,
  methodsOrIds: OrId<IMethod>[]
) {
  const checks: AnyCheck[] = [];

  const object = ensureObject(model.metamodel, objectOrId)!;
  const fsm = ensureFSM(object, fsmOrId)!;

  const sourceState = ensureState(fsm, sourceStateOrId)!;
  const targetState = ensureState(fsm, targetStateOrId)!;
  const methods = methodsOrIds.map((methodOrId) => ensureMethod(model.metamodel, methodOrId)!);

  const name = `${sourceState.name}${upperFirst(targetState.name)}`;

  const transition = Atomic.ACMC_Transition(fsm, name, sourceState, targetState, methods);

  checks.push(
    new NameCheck(model.metamodel, transition),
    new TransitionConsistencyCheck(model.metamodel, transition)
  );

  return {
    transition,
    checks,
  };
}

/**
 * Create a new inheritance relation.
 *
 * @param {Model} model The model to operate on.
 * @param {String or ObjectType} superTypeOrId The supertype (or its id).
 * @param {String or ObjectType} subTypeOrId The subtype (or its id).
 * @param {boolean} abstract Is the supertype abstract?
 * @param {Object} specialisations Information about how to specialise the event types/methods of the
 * supertype. The keys in the object are the id of a method in the supertype. The corresponding
 * values are one of:
 * - NONE: do nothing
 * - CREATE: create a new specialised event and a corresponding method
 * - id: the id of a method in the subtype to use.
 */
export function CCMC_Inheritance(
  model: IModel,
  superTypeOrId: OrId<IObjectType>,
  subTypeOrId: OrId<IObjectType>,
  abstract: boolean,
  specialisations: Specialisations
) {
  const checks: AnyCheck[] = [];
  const methods: IMethod[] = [];
  const events: IEventType[] = [];
  const transitions: ITransition[] = [];

  const superType = ensureObject(model.metamodel, superTypeOrId)!;
  const subType = ensureObject(model.metamodel, subTypeOrId)!;

  // 1. Create an inheritance relation between supertype and subtype
  const inheritance = Atomic.ACMC_Inheritance(model.metamodel, superType, subType);
  // The loop checks are model-wide (TElement defaults to IMetaModel in the
  // checks layer), but the legacy call passes the inheritance as the message
  // subject. Cast to the constructor's element param at this genuine checks-layer
  // gap; the element is only used as the success-message subject.
  checks.push(
    new InheritanceLoopCheck(
      model.metamodel,
      inheritance as unknown as ConstructorParameters<typeof InheritanceLoopCheck>[1]
    )
  );
  checks.push(
    new DependencyLoopCheck(
      model.metamodel,
      inheritance as unknown as ConstructorParameters<typeof DependencyLoopCheck>[1]
    )
  );
  // By adding an inheritance relation, we can introduce conflicts in the attributes of the subtype or its subtypes
  for (const attribute of subType.attributes.concat(
    subType.indirectSubtypes.flatMap((st) => st.attributes.slice())
  )) {
    checks.push(new ObjectAttributeNameConflictCheck(model.metamodel, attribute));
  }

  // 2. Make the supertype abstract/non-abstract if the user changed the setting
  if (superType.abstract !== abstract) {
    const { checks: extraChecks } = CEMC_Object(
      model,
      superType,
      {
        abstract,
      },
      false
    ); // set propagateRename to false as we don't rename
    checks.push(...extraChecks);
  }

  // 3. Make inherited methods in the subtype for all methods of the supertype (except inherited
  //    methods that are specialised)
  const methodsToInherit = superType.methods.filter((m) => !m.specialised);
  for (const superMethod of methodsToInherit) {
    // Don't create inherited methods for methods that are already looping
    const {
      checks: extraChecks,
      methods: extraMethods,
      transitions: extraTransitions,
      method: inheritedMethod,
    } = CCMC_InheritedMethod(
      model,
      superMethod.name,
      subType,
      superMethod.ownerEventType,
      superMethod.type,
      superMethod,
      inheritance,
      // don't propagate as we will do that manually below, after we created any specialisations
      false
    );
    checks.push(...extraChecks);
    methods.push(...extraMethods);
    transitions.push(...extraTransitions);

    // 3.1 Specialise the inherited method if the user indicated that.
    const requestedSpecialisation = specialisations[superMethod.id];
    if (requestedSpecialisation === 'CREATE') {
      // Create a specialised event
      const superEvent = superMethod.ownerEventType;
      let eventName = superEvent.name.replace(superType.name, subType.name);
      eventName = uniqueName(model.metamodel.eventTypes, eventName);

      const methodName = eventName.replace(/^EV/i, 'ME');
      const { event, checks: extraChecks } = CCMC_SpecialisedEvent(
        model,
        eventName,
        superEvent,
        inheritance
      );
      checks.push(...extraChecks);
      events.push(event);

      // Create a specialised method
      const {
        methods: extraMethods,
        transitions: extraTransitions,
        checks: extraMethodChecks,
      } = CCMC_SpecialisedMethod(
        model,
        methodName,
        subType,
        event,
        inheritedMethod.type,
        inheritedMethod,
        // don't propagate as we will do that manually below
        false
      );
      checks.push(...extraMethodChecks);
      methods.push(...extraMethods);
      transitions.push(...extraTransitions);
    } else if (requestedSpecialisation !== 'NONE' && requestedSpecialisation != null) {
      // requestedSpecialisation is the id of a method in the subtype
      // -> convert its event to a specialised event
      // -> convert it to a specialised method
      // The JMermaid code created a new event and a new method, then copied over
      // everything from the old event/method + updated all references to the new
      // event/method + removed the old event/method.
      // -> that seems like converting the event to a specialised event and the
      //    method to a specialised method to me. This wasn't possible directly
      //    since those are different subclasses in JMermaid. We can just convert
      //    the old event/method and don't bother with copying/updating references.
      const method = ensureMethod(model.metamodel, requestedSpecialisation)!;

      // Specialise the owner event type of the method
      const { checks: extraEventChecks } = Combined_SpecialiseEvent(
        model,
        method.ownerEventType,
        superMethod.ownerEventType,
        inheritance
      );
      checks.push(...extraEventChecks);

      // specialise the method
      const { checks: extraMethodChecks } = CEMC_SpecialiseMethod(model, method, inheritedMethod);
      checks.push(...extraMethodChecks);
    }
  }

  // 4. Propagate all methods of the subtype and of any further subtypes
  // protect against loops -> skip this if the supertype is existence dependent of the subtype
  if (!superType.isExistenceDependentOf(subType)) {
    for (const m of subType.methods) {
      const {
        checks: extraChecks,
        methods: extraMethods,
        transitions: extraTransitions,
      } = CCMC_AutoPropagateMethod(model, m);
      checks.push(...extraChecks);
      methods.push(...extraMethods);
      transitions.push(...extraTransitions);
    }
    for (const m of subType.indirectSubtypes.flatMap((st) => st.methods.slice())) {
      const {
        checks: extraChecks,
        methods: extraMethods,
        transitions: extraTransitions,
      } = CCMC_AutoPropagateMethod(model, m);
      checks.push(...extraChecks);
      methods.push(...extraMethods);
      transitions.push(...extraTransitions);
    }
  }

  return {
    checks,
    inheritance,
    events,
    methods,
    transitions,
  };
}
